import { api, type QueryParams } from '@/lib/api/client';
import type {
  ApiToken,
  ApiTokenOptions,
  MintedApiToken,
  Paginated,
  Role,
} from '@/lib/api/types';
import type { Permission } from '@/lib/rbac/permissions';

/**
 * API tokens: non-interactive credentials for machine clients (the LLM
 * connector, CI). Requires users:manage AND an interactive session — the
 * backend refuses these routes to a token, so a leaked credential can never
 * mint itself a new one.
 */

export interface CreateApiTokenBody {
  name: string;
  /** Bind to an existing service account… */
  service_account_id?: string;
  /** …or provision one alongside the token. Exactly one of the two. */
  service_account?: { name: string; role: Role };
  /** Narrows the role. Empty = everything the role grants. */
  scopes?: Permission[];
  /** Write allowlist by collection plural. Empty = every collection. */
  collections?: string[];
  /** 0 or omitted means no expiry (discouraged). */
  expires_in_days?: number;
}

export interface UpdateApiTokenBody {
  name?: string;
  scopes?: Permission[];
  collections?: string[];
  /** Re-dates the expiry from now; 0 clears it. Omit to leave it alone. */
  expires_in_days?: number;
}

export const apiTokensApi = {
  async list(params?: QueryParams): Promise<Paginated<ApiToken>> {
    const res = await api.getRaw<ApiToken[]>('/admin/api-tokens', params);
    return { data: res.data ?? [], meta: { pagination: res.meta!.pagination! } };
  },
  /**
   * The create dialog's bootstrap: which service accounts exist, which roles one
   * may hold and what each grants, and which collections can be allowlisted.
   * All derived server-side from the live RBAC matrix and collection registry,
   * so this UI can never offer a permission the server does not have.
   */
  options(): Promise<ApiTokenOptions> {
    return api.get<ApiTokenOptions>('/admin/api-tokens/options');
  },
  /** The response carries the plaintext secret. It is never readable again. */
  create(body: CreateApiTokenBody): Promise<MintedApiToken> {
    return api.post<MintedApiToken>('/admin/api-tokens', body);
  },
  update(id: string, body: UpdateApiTokenBody): Promise<ApiToken> {
    return api.patch<ApiToken>(`/admin/api-tokens/${id}`, body);
  },
  /** Revocation is immediate: the next call made with this token fails 401. */
  revoke(id: string): Promise<null> {
    return api.del(`/admin/api-tokens/${id}`);
  },
};
